import dayjs from 'dayjs';
import 'dayjs/locale/th';
import { formatCurrency } from '../utils/helpers';
import { loadImageBase64 } from "../utils/loadImageBase64";

// Set locale to Thai
dayjs.locale('th');

const initializePdfMake = () => {
    if (window.pdfMake) {
        window.pdfMake.fonts = {
            THSarabunNew: {
                normal: 'THSarabunNew.ttf',
                bold: 'THSarabunNew Bold.ttf',
                italics: 'THSarabunNew Italic.ttf',
                bolditalics: 'THSarabunNew BoldItalic.ttf'
            }
        };
    }
};

const createEntryContent = (entry, company, logoBase64) => {
    const tableBody = [
        [
            { text: 'รหัสบัญชี', style: 'tableHeader', alignment: 'center' },
            { text: 'ชื่อบัญชี', style: 'tableHeader', alignment: 'center' },
            { text: 'รายละเอียด', style: 'tableHeader', alignment: 'center' },
            { text: 'เดบิต', style: 'tableHeader', alignment: 'center' },
            { text: 'เครดิต', style: 'tableHeader', alignment: 'center' }
        ]
    ];

    let totalDebit = 0;
    let totalCredit = 0;

    entry.items.forEach(item => {
        const debit = parseFloat(item.debit || 0);
        const credit = parseFloat(item.credit || 0);
        totalDebit += debit;
        totalCredit += credit;

        tableBody.push([
            { text: item.account_code || item.ACCOUNT_CODE || '-', alignment: 'center' },
            { text: item.account_name || item.ACCOUNT_NAME || '-' },
            { text: item.description || '-' },
            { text: debit > 0 ? formatCurrency(debit) : '-', alignment: 'right' },
            { text: credit > 0 ? formatCurrency(credit) : '-', alignment: 'right' }
        ]);
    });

    tableBody.push([
        { text: 'รวมทั้งสิ้น', colSpan: 3, alignment: 'right', bold: true, fillColor: '#f0f0f0' },
        {}, {},
        { text: formatCurrency(totalDebit), alignment: 'right', bold: true, fillColor: '#f0f0f0' },
        { text: formatCurrency(totalCredit), alignment: 'right', bold: true, fillColor: '#f0f0f0' }
    ]);

    return [
        {
            columns: [
                logoBase64 ? {
                    image: logoBase64,
                    width: 60,
                    alignment: 'left'
                } : { text: '', width: 60 },
                {
                    stack: [
                        { text: company.company_name || company.name || 'บริษัท อะตอม ซอฟท์ จำกัด', style: 'header' },
                        { text: company.address || '-', alignment: 'center', fontSize: 12 },
                        { text: `เลขประจำตัวผู้เสียภาษี: ${company.tax_id || '-'}`, alignment: 'center', fontSize: 12 }
                    ],
                    width: '*'
                },
                { text: '', width: 60 }
            ]
        },
        { text: 'ใบสำคัญรายวันทั่วไป (General Journal Voucher)', style: 'title' },
        {
            columns: [
                {
                    width: '60%',
                    stack: [
                        {
                            columns: [
                                { text: 'รายละเอียด:', style: 'label', width: 60 },
                                { text: entry.description || '-' }
                            ]
                        },
                        {
                            columns: [
                                { text: 'อ้างอิง:', style: 'label', width: 60 },
                                { text: entry.reference_no || '-' }
                            ],
                            margin: [0, 5, 0, 0]
                        }
                    ]
                },
                {
                    width: '40%',
                    stack: [
                        {
                            columns: [
                                { text: 'เลขที่ JV:', style: 'label', width: 60 },
                                { text: entry.jv_number, bold: true }
                            ]
                        },
                        {
                            columns: [
                                { text: 'วันที่:', style: 'label', width: 60 },
                                { text: dayjs(entry.entry_date).format('DD/MM/YYYY') }
                            ],
                            margin: [0, 5, 0, 0]
                        }
                    ],
                    alignment: 'right'
                }
            ],
            margin: [0, 0, 0, 20]
        },
        {
            table: {
                headerRows: 1,
                widths: ['auto', 'auto', '*', 'auto', 'auto'],
                body: tableBody
            },
            layout: {
                hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 0.5,
                vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length) ? 1 : 0.5,
                hLineColor: () => '#cccccc',
                vLineColor: () => '#cccccc',
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 4,
                paddingBottom: () => 4
            },
            margin: [0, 0, 0, 40]
        },
        {
            columns: [
                {
                    width: '33%',
                    stack: [
                        { text: '__________________________', alignment: 'center' },
                        { text: 'ผู้จัดทำ', alignment: 'center', margin: [0, 5, 0, 0] },
                        { text: 'วันที่ ____/____/______', alignment: 'center', margin: [0, 5, 0, 0] }
                    ]
                },
                {
                    width: '33%',
                    stack: [
                        { text: '__________________________', alignment: 'center' },
                        { text: 'ผู้ตรวจสอบ', alignment: 'center', margin: [0, 5, 0, 0] },
                        { text: 'วันที่ ____/____/______', alignment: 'center', margin: [0, 5, 0, 0] }
                    ]
                },
                {
                    width: '34%',
                    stack: [
                        { text: '__________________________', alignment: 'center' },
                        { text: 'ผู้อนุมัติ', alignment: 'center', margin: [0, 5, 0, 0] },
                        { text: 'วันที่ ____/____/______', alignment: 'center', margin: [0, 5, 0, 0] }
                    ]
                }
            ]
        }
    ];
};

export const generateJournalEntryPDF = async (entries, company = {}) => {
    const entryList = Array.isArray(entries) ? entries : [entries];

    // Load logo
    const logoBase64 = company?.logo_path ? await loadImageBase64(`${process.env.REACT_APP_API_URL}/uploads/${company.logo_path}`) : null;

    if (!window.pdfMake) {
        throw new Error('pdfMake library is not loaded');
    }

    initializePdfMake();

    const content = [];
    entryList.forEach((entry, index) => {
        content.push(...createEntryContent(entry, company, logoBase64));
        if (index < entryList.length - 1) {
            content.push({ text: '', pageBreak: 'after' });
        }
    });

    const docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 40],
        defaultStyle: {
            font: 'THSarabunNew',
            fontSize: 14
        },
        styles: {
            header: {
                fontSize: 18,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 5]
            },
            tableHeader: {
                bold: true,
                fontSize: 14,
                color: 'black',
                fillColor: '#eeeeee'
            },
            label: {
                bold: true
            },
            title: {
                fontSize: 20,
                bold: true,
                alignment: 'center',
                margin: [0, 10, 0, 20]
            }
        },
        content: content
    };

    try {
        const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);
        return new Promise((resolve) => {
            pdfDocGenerator.getDataUrl((dataUrl) => {
                resolve(dataUrl);
            });
        });
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('ไม่สามารถสร้าง PDF ได้');
    }
};
